// A long text of a card (R-U6, decision 034): the core's block editor in place of a text box. Typing is saved by «record.update» a
// moment after the person stops, and at once when they leave the text; pictures and files go under the record. A save names the
// record's revision and a refusal keeps the text with its reason (text-save.ts, audit 31.10 P2-6).

import { useEffect, useMemo, useRef, useState } from 'react';
import { recordFileUrl, type FieldDef, type Id, type RecordObj } from '@confdeck/core-shared';
import { BlockEditor, type EditorFiles } from '../editor/BlockEditor.tsx';
import { useSync } from '../sync/react.tsx';
import { Button, useToast } from '../ui/primitives.tsx';
import { useWritable } from './Prop.tsx';
import { TextSaver } from './text-save.ts';
import s from './record.module.css';

/** How long after the last key the text is sent. */
const SAVE_DELAY = 700;

/** The card mounts one LongText per record and field (RecordView keys it so): the saver belongs to that pair. */
export function LongText({ rec, field, onOpen }: { rec: RecordObj; field: FieldDef; onOpen: (id: Id) => void }) {
  const client = useSync();
  const toast = useToast();
  // A text the app's commands write (the files of an application, R-F6) is read here, not edited (L5 of the card: managed fields).
  const writable = useWritable(rec) && !field.options.managed;
  const value = String(rec.data[field.key] ?? '');
  const saver = useMemo(() => new TextSaver({ run: (c, i) => client.run(c, i), confirmed: (id) => client.store.confirmedGet(id) as { rev: number; data: Record<string, unknown> } | undefined }, rec.id, field.key), []);
  const [problem, setProblem] = useState(saver.problem);
  // The editor is drawn anew when the person takes the colleague's text instead of theirs.
  const [round, setRound] = useState(0);
  const mounted = useRef(true);
  saver.onState = () => {
    if (mounted.current) setProblem(saver.problem);
    // The card is closed already: the reason goes to a toast, the text is in the record's versions only if it was saved.
    else if (saver.problem) toast({ kind: 'error', text: saver.problem.message });
  };
  const pending = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const flush = () => {
    clearTimeout(timer.current);
    if (pending.current === null) return;
    const md = pending.current;
    pending.current = null;
    void saver.save(md);
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;
  // Leaving the card sends what was typed.
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; flushRef.current(); }; }, []);
  useEffect(() => { const on = () => flushRef.current(); window.addEventListener('pagehide', on); return () => window.removeEventListener('pagehide', on); }, []);

  const files: EditorFiles = {
    urlFor: (file, slot) => recordFileUrl(rec.id, slot, file.name),
    async send(file, slot) {
      // The text naming the file goes first: the server keeps only a file the record's text names (files/attach.ts).
      flushRef.current();
      for (let i = 0; i < 150 && (saver.saving || client.unsent > 0); i++) await new Promise((r) => setTimeout(r, 200));
      const res = await fetch(`/api/records/${rec.id}/embed?field=${field.key}&slot=${slot}`, { method: 'POST', headers: { 'content-type': 'application/octet-stream', 'x-file-name': encodeURIComponent(file.name) }, body: file }).catch(() => null);
      if (res?.ok) return true;
      const body = await res?.json().catch(() => ({})) as { error?: { message: string } } | undefined;
      toast({ kind: 'error', text: body?.error?.message ?? 'Файл загрузить не удалось' });
      return false;
    },
  };

  return (
    <section className={s.block}>
      <h2 className={s.blockTitle}>{field.name}</h2>
      {!writable && !value.trim() ? <div className={s.empty}>Пусто</div> : <BlockEditor key={round} value={value} label={field.name} readOnly={!writable} files={files} onOpenRecord={onOpen}
        // A text that came from elsewhere waits while this one is not saved: it would replace what the person typed.
        hold={() => pending.current !== null || saver.saving || !!saver.problem} onTake={() => saver.take()}
        onChange={(md, now) => { pending.current = md; clearTimeout(timer.current); if (now) flush(); else timer.current = setTimeout(() => flushRef.current(), SAVE_DELAY); }} />}
      {problem && (
        <div className={s.saveProblem} role="alert">
          <span>{problem.message}</span>
          <Button size="sm" variant="secondary" onClick={() => void saver.retry()}>{problem.kind === 'conflict' ? 'Сохранить мой вариант' : 'Повторить'}</Button>
          {problem.kind === 'conflict' && <Button size="sm" onClick={() => { pending.current = null; clearTimeout(timer.current); saver.drop(); setRound((n) => n + 1); }}>Взять их текст</Button>}
        </div>
      )}
    </section>
  );
}
